import { computed, inject } from '@angular/core';
import { DeliveryReceiptDataService } from '@lib/delivery-receipt/data-access';
import { IOrderItemViewDto, OrderDataService } from '@lib/order/data-access';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { debounceTime, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { IDeliveryReceiptResponse, IDeliveryReceiptSummary, IReceiptItem } from './types';
import { supplierDisplayName } from '@lib/supplier';

export type SearchResult = {
    id: number;
    receiptNumber: string;
    documentDate: string;
    supplierName: string;
    supplierNumber?: string | null;
    supplierDisplayName: string;
    isOpen: boolean;
    inApproval: boolean;
};

interface LinkDeliveryReceiptDialogState {
    supplierId: string;
    search: string;
    searching: boolean;
    includeClosed: boolean;
    results: SearchResult[];
    selectedReceiptId: number | null;
    selectedReceiptNumber: string;
    receiptItems: IReceiptItem[];
    loadingItems: boolean;
    selectedItemId: number | null;
    selectedItemPosition: number | null;
    // Order item data loaded from the order service
    selectedOrderItem: IOrderItemViewDto | null;
    loadingOrderItem: boolean;
    // Prefetch map of receiptId -> isOpen status for showing in search results
    statusMap: Record<number, boolean>;
    // Summaries for the header section of already linked receipts
    linkedSummaries: IDeliveryReceiptSummary[];
    // When opening with an already linked item, remember it until items are loaded
    pendingSelectItemId: number | null;
    pendingSelectItemPosition: number | null;
}

const initialState: LinkDeliveryReceiptDialogState = {
    supplierId: '',
    search: '',
    searching: false,
    includeClosed: false,
    results: [],
    selectedReceiptId: null,
    selectedReceiptNumber: '',
    receiptItems: [],
    loadingItems: false,
    selectedItemId: null,
    selectedItemPosition: null,
    selectedOrderItem: null,
    loadingOrderItem: false,
    statusMap: {},
    linkedSummaries: [],
    pendingSelectItemId: null,
    pendingSelectItemPosition: null,
};

export const LinkDeliveryReceiptDialogStore = signalStore(
    withState(initialState),
    withComputed((store) => ({
        isInItemSelection: computed(() => store.selectedReceiptId() !== null),
        isInReceiptSearch: computed(() => store.selectedReceiptId() === null),
        selectedReceipt: computed(() => {
            const selectedId = store.selectedReceiptId();
            if (!selectedId) return null;
            return store.results().find((r) => r.id === selectedId) || null;
        }),
        selectedReceiptStatus: computed(() => {
            const selectedReceipt = store.results().find((r) => r.id === store.selectedReceiptId());
            return selectedReceipt?.isOpen ?? null;
        }),
        selectedReceiptInApproval: computed(() => {
            const selectedReceipt = store.results().find((r) => r.id === store.selectedReceiptId());
            return selectedReceipt?.inApproval ?? false;
        }),
        selectedItem: computed(() => {
            const selectedId = store.selectedItemId();
            if (!selectedId) return null;
            return store.receiptItems().find((item) => item.id === selectedId) || null;
        }),
    })),
    withMethods((store, drService = inject(DeliveryReceiptDataService), orderService = inject(OrderDataService)) => {
        // Define rxMethods as local consts and return them to avoid referencing via store.* from inside methods
        const searchReceipts = rxMethod<string>((source$) =>
            source$.pipe(
                debounceTime(250),
                switchMap((term) => {
                    const supplier = store.supplierId();
                    if (!supplier) {
                        patchState(store, { results: [], searching: false });
                        return of([] as SearchResult[]);
                    }
                    patchState(store, { searching: true });
                    return drService.searchBySupplier(supplier, term, store.includeClosed()).pipe(
                        map((results) =>
                            results.map((result) => ({
                                ...result,
                                supplierDisplayName: supplierDisplayName(result.supplierNumber, result.supplierName),
                            })),
                        ),
                        tap((results) => {
                            patchState(store, { results, searching: false });
                        }),
                    );
                }),
            ),
        );

        const loadLinkedSummaries = rxMethod<{ receiptId: number; receiptNumber: string }[]>((list$) =>
            list$.pipe(
                switchMap((list) => {
                    if (!list || list.length === 0) {
                        return of([]);
                    }
                    return forkJoin(list.map((l) => drService.getById(l.receiptId)));
                }),
                map((responses: IDeliveryReceiptResponse[]) =>
                    responses.map((res) => ({
                        id: res.data.id,
                        receiptNumber: res.data.number,
                        supplierName: res.data.supplier?.companyAddress?.companyName?.line1 ?? '',
                        supplierNumber: res.data.supplier?.supplierNumber,
                        supplierDisplayName: supplierDisplayName(
                            res.data.supplier?.supplierNumber,
                            res.data.supplier?.companyAddress?.companyName?.line1 ?? '',
                        ),
                        isOpen: res.data.open as boolean,
                        inApproval: res.data.inApproval as boolean,
                    })),
                ),
                tap((summaries: IDeliveryReceiptSummary[]) =>
                    patchState(store, {
                        linkedSummaries: summaries,
                    }),
                ),
            ),
        );

        const prefetchStatuses = rxMethod<SearchResult[]>((results$) =>
            results$.pipe(
                map((arr) => arr.slice(0, 10)),
                switchMap((arr) =>
                    arr.length
                        ? forkJoin(arr.map((r) => drService.getById(r.id)))
                        : of([] as IDeliveryReceiptResponse[]),
                ),
                tap((responses: IDeliveryReceiptResponse[]) => {
                    const current = { ...store.statusMap() };
                    for (const res of responses) {
                        if (res && res.data) current[res.data.id] = !!res.data.open;
                    }
                    patchState(store, { statusMap: current });
                }),
            ),
        );

        const loadItems = rxMethod<number>((ids$) =>
            ids$.pipe(
                tap(() => patchState(store, { loadingItems: true })),
                switchMap((id) => drService.getById(id)),
                map(({ data }) =>
                    (data.items || []).map((it) => ({
                        id: it.id,
                        name: it.name,
                        note: it.shortDescription || it.note,
                        articleNumberSupplier: it.articleNumberSupplier,
                        quantity: it.quantity,
                        positionReceipt: it.positionReceipt,
                        orderUnit: it.orderUnit,
                        contentUnit: it.contentUnit,
                        numberOfContentUnitsPerOrderUnit: it.numberOfContentUnitsPerOrderUnit,
                        shortDescription: it.shortDescription,
                        orderItemId: it.orderItemId,
                    })),
                ),
                tap({
                    next: (items) => {
                        const typedItems = items as LinkDeliveryReceiptDialogState['receiptItems'];
                        patchState(store, { receiptItems: typedItems, loadingItems: false });
                        const pendingId = store.pendingSelectItemId();
                        if (pendingId) {
                            const found = typedItems.find((x) => x.id === pendingId);
                            if (found) {
                                patchState(store, {
                                    selectedItemId: found.id,
                                    selectedItemPosition: found.positionReceipt,
                                });
                            }
                            patchState(store, {
                                pendingSelectItemId: null,
                                pendingSelectItemPosition: null,
                            });
                        }
                    },
                    error: () => patchState(store, { loadingItems: false }),
                }),
            ),
        );

        const loadOrderItem = rxMethod<number>((orderItemId$) =>
            orderItemId$.pipe(
                tap(() => patchState(store, { loadingOrderItem: true, selectedOrderItem: null })),
                switchMap((orderItemId) => orderService.getOrderItemById(orderItemId)),
                tap({
                    next: (orderItem: IOrderItemViewDto) => {
                        patchState(store, { selectedOrderItem: orderItem, loadingOrderItem: false });
                    },
                    error: () => patchState(store, { loadingOrderItem: false }),
                }),
            ),
        );

        return {
            init(supplierId: string) {
                patchState(store, { supplierId });
                // trigger initial fetch of last 50 receipts
                searchReceipts('');
            },
            setSearch(term: string) {
                patchState(store, { search: term });
                searchReceipts(term);
            },
            setIncludeClosed(includeClosed: boolean) {
                patchState(store, { includeClosed });
            },
            searchReceipts,
            setResults(results: SearchResult[]) {
                patchState(store, { results, searching: false });
            },
            selectReceipt(r: SearchResult) {
                patchState(store, { selectedReceiptId: r.id, selectedReceiptNumber: r.receiptNumber });
                loadItems(r.id);
            },
            backToReceipts() {
                patchState(store, {
                    selectedReceiptId: null,
                    selectedReceiptNumber: '',
                    receiptItems: [],
                    selectedItemId: null,
                    selectedItemPosition: null,
                });
            },
            pickItem(item: { id: number; positionReceipt: number }) {
                patchState(store, { selectedItemId: item.id, selectedItemPosition: item.positionReceipt });
                // Order item will be loaded only when dialog is confirmed
            },
            deselectItem() {
                patchState(store, {
                    selectedItemId: null,
                    selectedItemPosition: null,
                    selectedOrderItem: null,
                    loadingOrderItem: false,
                });
            },
            // Preselect currently linked relation when opening the dialog
            preselectCurrent(relation: {
                deliveryReceiptId: number;
                deliveryReceiptNumber: string;
                deliveryItemId: number;
                deliveryItemPosition: number;
                supplierName?: string;
                supplierNumber?: string | null;
            }) {
                patchState(store, {
                    selectedReceiptId: relation.deliveryReceiptId,
                    selectedReceiptNumber: relation.deliveryReceiptNumber,
                    pendingSelectItemId: relation.deliveryItemId,
                    pendingSelectItemPosition: relation.deliveryItemPosition,
                });

                // If supplier name is provided, add this receipt to results with supplier info
                if (relation.supplierName) {
                    const existingResult = store.results().find((r) => r.id === relation.deliveryReceiptId);
                    if (!existingResult) {
                        const newResult: SearchResult = {
                            id: relation.deliveryReceiptId,
                            receiptNumber: relation.deliveryReceiptNumber,
                            supplierName: relation.supplierName,
                            supplierDisplayName: supplierDisplayName(relation.supplierNumber, relation.supplierName),
                            documentDate: '', // Will be loaded when full receipt data is fetched
                            isOpen: false, // Will be loaded when full receipt data is fetched
                            inApproval: false, // Will be loaded when full receipt data is fetched
                        };
                        patchState(store, {
                            results: [...store.results(), newResult],
                        });
                    }
                }

                loadItems(relation.deliveryReceiptId);
            },
            // Fetch summaries for already linked receipts (supplier + status)
            loadLinkedSummaries,
            // Set basic info for linked receipts and load supplier information
            setLinkedReceiptBasicInfo(receipts: { receiptId: number; receiptNumber: string }[]) {
                const basicSummaries = receipts.map((r) => ({
                    id: r.receiptId,
                    receiptNumber: r.receiptNumber,
                    supplierName: '', // Will be loaded immediately below
                    supplierDisplayName: '',
                    isOpen: false, // Will be loaded immediately below
                    inApproval: false, // Will be loaded immediately below
                }));
                patchState(store, { linkedSummaries: basicSummaries });

                // Load supplier information for all linked receipts
                if (receipts.length > 0) {
                    loadLinkedSummaries(receipts);
                }
            },
            // Load receipt details and select it (for linked summaries)
            loadReceiptDetailsAndSelect(receiptId: number, receiptNumber: string) {
                patchState(store, { selectedReceiptId: receiptId, selectedReceiptNumber: receiptNumber });

                // Load full receipt details and update the linked summary
                drService.getById(receiptId).subscribe((response) => {
                    const receiptData = response.data;
                    const updatedSummaries = store.linkedSummaries().map((summary) =>
                        summary.id === receiptId
                            ? {
                                  ...summary,
                                  supplierName: receiptData.supplier?.companyAddress?.companyName?.line1 ?? '',
                                  supplierNumber: receiptData.supplier?.supplierNumber,
                                  supplierDisplayName: supplierDisplayName(
                                      receiptData.supplier?.supplierNumber,
                                      receiptData.supplier?.companyAddress?.companyName?.line1 ?? '',
                                  ),
                                  isOpen: !!receiptData.open,
                                  inApproval: !!receiptData.inApproval,
                              }
                            : summary,
                    );
                    patchState(store, { linkedSummaries: updatedSummaries });

                    // Update status map for this receipt
                    const currentStatusMap = { ...store.statusMap() };
                    currentStatusMap[receiptId] = !!receiptData.open;
                    patchState(store, { statusMap: currentStatusMap });
                });

                loadItems(receiptId);
            },
            // Prefetch open/closed status for search results
            prefetchStatuses,
            loadItems,
            loadOrderItem,
            setItems(items: LinkDeliveryReceiptDialogState['receiptItems']) {
                patchState(store, { receiptItems: items, loadingItems: false });
            },
            setItemsLoadError() {
                patchState(store, { loadingItems: false });
            },
        };
    }),
);
